import { useState } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { useParams, useRevalidator } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { getOption, useParticipantStatusOptions } from '../../../../utils'
import { Participants, ParticipantStatus } from '../../../../types'
import { usePatchParticipantMutation } from '../../../../services/events'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '50rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  currentParticipants: Map<number, Participants<true>>
  name: string | undefined
}

export default function BulkEditParticipants({ open, setOpen, name, currentParticipants }: Props) {
  const { t } = useTranslation('admin')
  const revalidator = useRevalidator()

  const [patchParticipant, { isLoading }] = usePatchParticipantMutation()

  const [lockedStatus, setLockedStatus] = useState(true)
  const [lockedPresent, setLockedPresent] = useState(true)

  const participantStatusOptions = useParticipantStatusOptions(t).filter(
    option => option.value !== ParticipantStatus.Cancelled
  )
  const { eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const handleClose = () => {
    setOpen(false)
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.target as HTMLFormElement
    const formData = new FormData(form)

    const submitAction = Promise.all(
      [...currentParticipants].map(async ([, participation]) => {
        const parsedData = {
          status: lockedStatus
            ? participation.status
            : (getOption(participantStatusOptions, formData.get('status') as string)?.value ??
              participation.status),
          present: lockedPresent
            ? participation.present
            : (formData.get('present') as string) === 'on',
        }

        const res = await patchParticipant({
          eventId: Number(eventId),
          id: participation.id,
          data: parsedData,
        })

        return res
      })
    )

    submitAction
      .then(data => {
        revalidator.revalidate()

        const anyError = data.filter(res => 'error' in res)
        if (anyError.length) {
          toast.error(
            `${t('Une erreur est survenue, certaines données peuvent ne pas avoir été modifiées')}.`
          )
        } else {
          setOpen(false)
          setLockedStatus(true)
          setLockedPresent(true)
          form.reset()
          toast.success(t('Inscriptions modifiées'))
        }
      })
      .catch(() => {
        toast.error(
          `${t('Une erreur est survenue, certaines données peuvent ne pas avoir été modifiées')}.`
        )
      })
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Title>{t('Édition groupée des inscriptions')}</Title>
            <Divider />
          </Stack>
          <Stack gap="1rem" flex="1 0" margin="2rem 0">
            <h5 style={{ marginBlock: 0 }}>
              {t(
                'Sélectionnez les champs à activer et modifiez la valeur, seuls les champs actifs seront modifiés en base de données'
              )}
              .
            </h5>
            <Stack direction="row" gap="1rem">
              <IconButton onClick={() => setLockedStatus(prev => !prev)}>
                {lockedStatus ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
              <Autocomplete
                disablePortal
                id="status-autocomplete"
                options={participantStatusOptions}
                fullWidth
                disableClearable
                disabled={lockedStatus}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t("Statut d'inscription")}
                    name="status"
                    variant={lockedStatus ? 'filled' : 'outlined'}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" gap="1rem">
              <IconButton onClick={() => setLockedPresent(prev => !prev)}>
                {lockedPresent ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
              <FormControlLabel
                name="present"
                control={<Checkbox disabled={lockedPresent} />}
                label={t('Présent')}
              />
            </Stack>

            <h3 style={{ marginBlock: 0 }}>{name}</h3>
            <span>
              {currentParticipants.size} {t('modification(s) en cours')}
            </span>
            <ul>
              {[...currentParticipants].map(([, participation]) => (
                <li key={participation.id}>
                  {`${participation.last_name} ${participation.first_name}`}
                </li>
              ))}
            </ul>
          </Stack>
          <Stack direction="row" justifyContent="space-between" paddingBottom="1.5rem">
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
            >
              {t('Annuler')}
            </Button>
            <LoadingButton variant="contained" type="submit" loading={isLoading}>
              {'Modifier'}
            </LoadingButton>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
